// ─── Template: image-reveal-v1 ─────────────────────────────────────────────
// An image that reveals via a wipe / slide / zoom mask, optionally with a
// caption block that slides up. Pairs a user-provided image with title +
// subtitle text.

import React from 'react';
import { AbsoluteFill, Easing, Img, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import type { AnimationTemplate } from '../types';

export type AlignPreset = 'left' | 'center' | 'right';
export type RevealStyle = 'wipe' | 'zoom' | 'slide-up' | 'fade' | 'none';
const FONT_STACK = 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export type ImageRevealProps = {
  /** URL of the image. Resolved by the editor's image picker. */
  imageUrl: string;
  /** Reveal animation. */
  reveal: RevealStyle;
  /** When true, image stays slowly zooming after reveal (Ken Burns). */
  kenBurns: boolean;
  /** When true, the image fills the whole frame; otherwise it sits inside
   *  a 76%-wide rounded card centered on the frame. */
  fullBleed: boolean;
  /** Cómo encaja la imagen en su caja: 'cover' recorta para llenar, 'contain'
   *  la muestra entera sin recortar (ideal para logos). */
  fit: 'cover' | 'contain';
  /** Caption text — title and subtitle below the image. */
  title: string;
  subtitle: string;
  titleFont: string;
  titleSize: number;
  titleColor: string;
  titleBold: boolean;
  titleItalic: boolean;
  titleAlign: AlignPreset;
  subtitleFont: string;
  subtitleSize: number;
  subtitleColor: string;
  subtitleBold: boolean;
  subtitleItalic: boolean;
  subtitleAlign: AlignPreset;
  background: string;
};

const Component: React.FC<ImageRevealProps> = ({
  imageUrl, reveal, kenBurns, fullBleed, fit = 'cover',
  title, subtitle,
  titleFont, titleSize, titleColor, titleBold, titleItalic, titleAlign,
  subtitleFont, subtitleSize, subtitleColor, subtitleBold, subtitleItalic, subtitleAlign,
  background,
}) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  // Reveal progress 0 → 1 over first ~24 frames.
  const r = interpolate(frame, [0, 24], [0, 1], {
    easing: Easing.out(Easing.cubic), extrapolateRight: 'clamp',
  });

  // Ken-Burns zoom continues subtly throughout the clip.
  const kbScale = kenBurns
    ? interpolate(frame, [0, 90], [1, 1.08], { extrapolateRight: 'clamp' })
    : 1;

  // Each reveal style sets a different clipPath / transform for the image.
  let imgStyle: React.CSSProperties = {};
  if (reveal === 'none') {
    // Sin animación de entrada: la imagen está visible desde el frame 0.
    // Útil para overlays como un logo, donde un reveal que lo esconde al
    // inicio se ve como un glitch apenas se inserta.
    imgStyle = {};
  } else if (reveal === 'wipe') {
    imgStyle = { clipPath: `inset(0 ${(1 - r) * 100}% 0 0)` };
  } else if (reveal === 'zoom') {
    imgStyle = { transform: `scale(${0.85 + r * 0.15 + (kbScale - 1)})`, opacity: r };
  } else if (reveal === 'slide-up') {
    imgStyle = { transform: `translateY(${(1 - r) * 60}px)`, opacity: r };
  } else {
    imgStyle = { opacity: r };
  }

  // Caption fades + slides up after the image is mostly revealed.
  const capOp = interpolate(frame, [22, 36], [0, 1], { extrapolateRight: 'clamp' });
  const capY = interpolate(frame, [22, 36], [16, 0], {
    easing: Easing.out(Easing.cubic), extrapolateRight: 'clamp',
  });

  // Caption sobre el lado más corto: el texto bajo la imagen ahora se
  // ve igual de grande (proporcionalmente) en horizontal y en vertical.
  const refDim = Math.min(width, height);
  const titlePx = (titleSize / 100) * refDim;
  const subtitlePx = (subtitleSize / 100) * refDim;

  // Figure out image box geometry.
  const imgWidth = fullBleed ? width : width * 0.76;
  const imgHeight = fullBleed ? height : height * 0.62;
  const imgRadius = fullBleed ? 0 : 16;

  return (
    <AbsoluteFill style={{
      background, fontFamily: FONT_STACK,
      alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
    }}>
      <div style={{
        width: imgWidth, height: imgHeight,
        borderRadius: imgRadius, overflow: 'hidden', position: 'relative',
        boxShadow: fullBleed || background === 'transparent' ? 'none' : '0 12px 32px rgba(0,0,0,0.25)',
      }}>
        {imageUrl ? (
          <Img
            src={imageUrl}
            style={{
              width: '100%', height: '100%', objectFit: fit,
              transform: kenBurns && reveal !== 'zoom' ? `scale(${kbScale})` : undefined,
              ...imgStyle,
            }}
          />
        ) : (
          // Placeholder when no image is selected yet.
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#1F2937', color: '#9CA3AF', fontSize: 24,
            border: '2px dashed #4B5563',
          }}>
            Elegí una imagen en el panel de la derecha
          </div>
        )}
      </div>
      {(title || subtitle) && !fullBleed && (
        <div style={{
          marginTop: height * 0.04,
          width: imgWidth,
          opacity: capOp,
          transform: `translateY(${capY}px)`,
        }}>
          {title && (
            <p style={{
              margin: 0,
              fontFamily: `${titleFont}, ${FONT_STACK}`,
              fontSize: titlePx,
              color: titleColor,
              fontWeight: titleBold ? 800 : 600,
              fontStyle: titleItalic ? 'italic' : 'normal',
              textAlign: titleAlign,
              lineHeight: 1.1,
            }}>{title}</p>
          )}
          {subtitle && (
            <p style={{
              margin: `${titlePx * 0.18}px 0 0`,
              fontFamily: `${subtitleFont}, ${FONT_STACK}`,
              fontSize: subtitlePx,
              color: subtitleColor,
              fontWeight: subtitleBold ? 600 : 400,
              fontStyle: subtitleItalic ? 'italic' : 'normal',
              textAlign: subtitleAlign,
              lineHeight: 1.3,
            }}>{subtitle}</p>
          )}
        </div>
      )}
    </AbsoluteFill>
  );
};

const ALIGN_OPTIONS = [
  { value: 'left',   label: 'Izquierda' },
  { value: 'center', label: 'Centro' },
  { value: 'right',  label: 'Derecha' },
];
const REVEAL_OPTIONS = [
  { value: 'none',     label: 'Sin animación' },
  { value: 'wipe',     label: 'Cortina' },
  { value: 'zoom',     label: 'Acercamiento' },
  { value: 'slide-up', label: 'Desliza hacia arriba' },
  { value: 'fade',     label: 'Aparece' },
];

export const imageRevealTemplate: AnimationTemplate<ImageRevealProps> = {
  id: 'image-reveal-v1',
  label: 'Imagen animada',
  description: 'Una imagen aparece con una máscara animada, opcionalmente con texto debajo.',
  category: 'image',
  defaultDurationSec: 4,
  defaultProps: {
    imageUrl: '',
    reveal: 'wipe',
    kenBurns: true,
    fullBleed: false,
    fit: 'cover',
    title: 'Tu producto',
    subtitle: 'Una descripción breve y atractiva.',
    titleFont: 'Inter', titleSize: 4, titleColor: '#FFFFFF',
    titleBold: true, titleItalic: false, titleAlign: 'center',
    subtitleFont: 'Inter', subtitleSize: 2.4, subtitleColor: '#A3A3A3',
    subtitleBold: false, subtitleItalic: false, subtitleAlign: 'center',
    background: '#0B0F1A',
  },
  propsSchema: {
    imageUrl: { type: 'image', label: 'Imagen' },
    reveal:   { type: 'select', label: 'Estilo de entrada', options: REVEAL_OPTIONS },
    kenBurns: { type: 'boolean', label: 'Movimiento suave de la cámara' },
    fullBleed:{ type: 'boolean', label: 'Imagen a pantalla completa' },
    fit: { type: 'select', label: 'Ajuste de imagen', options: [
      { value: 'cover',   label: 'Cubrir (recorta)' },
      { value: 'contain', label: 'Contener (entera)' },
    ] },
    title: {
      type: 'text', label: 'Título', placeholder: 'Título de la imagen', agentExposed: true,
      styling: {
        font: 'titleFont', size: 'titleSize', color: 'titleColor',
        bold: 'titleBold', italic: 'titleItalic', align: 'titleAlign',
      },
    },
    subtitle: {
      type: 'text', label: 'Subtítulo', placeholder: 'Texto secundario (opcional)', agentExposed: true,
      styling: {
        font: 'subtitleFont', size: 'subtitleSize', color: 'subtitleColor',
        bold: 'subtitleBold', italic: 'subtitleItalic', align: 'subtitleAlign',
      },
    },
    titleFont:   { type: 'font',    label: 'Tipografía' },
    titleSize:   { type: 'number',  label: 'Tamaño', min: 2, max: 12, step: 0.2, decimals: 1, unit: '%' },
    titleColor:  { type: 'color',   label: 'Color' },
    titleBold:   { type: 'boolean', label: 'Negrita' },
    titleItalic: { type: 'boolean', label: 'Cursiva' },
    titleAlign:  { type: 'select',  label: 'Alineación', options: ALIGN_OPTIONS },
    subtitleFont:   { type: 'font',    label: 'Tipografía' },
    subtitleSize:   { type: 'number',  label: 'Tamaño', min: 1.5, max: 6, step: 0.1, decimals: 1, unit: '%' },
    subtitleColor:  { type: 'color',   label: 'Color' },
    subtitleBold:   { type: 'boolean', label: 'Negrita' },
    subtitleItalic: { type: 'boolean', label: 'Cursiva' },
    subtitleAlign:  { type: 'select',  label: 'Alineación', options: ALIGN_OPTIONS },
    background: { type: 'color', label: 'Fondo', allowTransparent: true },
  },
  Component,
};
